import React, { type RefObject } from 'react';

import type { RawInsertBlockAnalyticsEventProps } from '@gitbook/app-analytics';
import {
    BLOCK_CODE,
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HINT,
    BLOCK_IMAGES,
    code,
    expandable,
    hint,
    images,
} from '@gitbook/doc-core';
import { Block, type Change } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { type ContentEditorProps, useInsertFiles } from '../../../Components';

type FocusType = (change: Change, block: Block) => void;
type InsertBlockCallback = (content: Block, focus: FocusType) => void;

type SpaceQuickActionsProps = {
    editorRef: RefObject<EditorContainer>;
} & Pick<ContentEditorProps, 'logAnalyticsEvent'>;

export const SpaceQuickActions = React.memo((props: SpaceQuickActionsProps) => {
    const { editorRef, logAnalyticsEvent } = props;

    const logInsertBlock = useEventCallback((props: RawInsertBlockAnalyticsEventProps) => {
        logAnalyticsEvent?.({
            type: 'edit_page_document_insert_block',
            from: 'quick-insert',
            ...props,
        });
    });

    // Insert a block into the editor.
    const insertBlock = useEventCallback((block: Block, focus: FocusType) => {
        editorRef.current?.change((change) => {
            change.collapseToStartOf(change.value.document).focus();

            const next = change.insertOrReplaceBlock(block);
            focus(next, block);
        });
    });

    return (
        <div className="mt-3 flex w-full flex-wrap gap-3">
            <SpaceActionHeading insertBlock={insertBlock} logInsertBlock={logInsertBlock} />
            <SpaceActionImage insertBlock={insertBlock} logInsertBlock={logInsertBlock} />
            <SpaceActionHint insertBlock={insertBlock} logInsertBlock={logInsertBlock} />
            <SpaceActionExpandable insertBlock={insertBlock} logInsertBlock={logInsertBlock} />
            <SpaceActionCode insertBlock={insertBlock} logInsertBlock={logInsertBlock} />
        </div>
    );
});

type ActionProps = {
    insertBlock: InsertBlockCallback;
    logInsertBlock: (props: RawInsertBlockAnalyticsEventProps) => void;
};

function SpaceActionHeading(props: ActionProps) {
    const { insertBlock, logInsertBlock } = props;

    const handleInsertHeading = () => {
        logInsertBlock({
            blockType: BLOCK_HEADING_1,
        });

        insertBlock(Block.create({ type: BLOCK_HEADING_1 }), (change) => {
            change.collapseToStartOfBlock().focus();
        });
    };

    return (
        <Button
            label="Heading 1"
            kind="pill"
            className="text-muted"
            leadingIcon={Icon.Heading1}
            onPress={handleInsertHeading}
        />
    );
}

function SpaceActionImage(props: ActionProps) {
    const { insertBlock, logInsertBlock } = props;
    const { prompt } = useInsertFiles();

    const handleInsertImage = async () => {
        const fileRefs = await prompt({
            title: 'Select image',
            accept: 'images',
            acceptURL: true,
        });

        if (fileRefs.length === 0) {
            return;
        }

        logInsertBlock({
            blockType: BLOCK_IMAGES,
            images: fileRefs.length,
        });

        insertBlock(
            Block.create({
                type: BLOCK_IMAGES,
                isVoid: true,
                nodes: fileRefs.map((selectedFile) => {
                    return images.createImageBlock(selectedFile);
                }),
            }),
            (change) => {
                change.collapseToStartOfBlock().focus();
            }
        );
    };

    return (
        <Button
            label="Image"
            kind="pill"
            className="text-muted"
            leadingIcon={Icon.Image}
            onPress={handleInsertImage}
        />
    );
}

function SpaceActionHint(props: ActionProps) {
    const { insertBlock, logInsertBlock } = props;

    const handleInsertHint = () => {
        logInsertBlock({
            blockType: BLOCK_HINT,
        });

        insertBlock(hint.createHint(), (change) => {
            change.collapseToStartOfBlock().focus();
        });
    };

    return (
        <Button
            label="Hint"
            kind="pill"
            className="text-muted"
            leadingIcon={Icon.InfoCircle}
            onPress={handleInsertHint}
        />
    );
}

function SpaceActionExpandable(props: ActionProps) {
    const { insertBlock, logInsertBlock } = props;

    const handleInsertExpandable = () => {
        logInsertBlock({
            blockType: BLOCK_EXPANDABLE,
        });

        insertBlock(expandable.createExpandableBlock(), (change, block) => {
            change.collapseToStartOf(expandable.getTitleFragment(block)).focus();
        });
    };

    return (
        <Button
            label="Expandable"
            kind="pill"
            className="text-muted"
            leadingIcon={Icon.BlockExpandable}
            onPress={handleInsertExpandable}
        />
    );
}

function SpaceActionCode(props: ActionProps) {
    const { insertBlock, logInsertBlock } = props;

    const handleInsertCode = () => {
        logInsertBlock({
            blockType: BLOCK_CODE,
        });

        insertBlock(code.createCodeBlock('// Some code'), (change, block) => {
            const firstText = block.getFirstText();
            if (firstText) {
                change.collapseToEndOf(firstText).focus();
            }
        });
    };

    return (
        <Button
            label="Code"
            kind="pill"
            className="text-muted"
            leadingIcon={Icon.Code}
            onPress={handleInsertCode}
        />
    );
}
